import { Stack, alpha, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useEffect } from "react";
import { useSubmitQuestionQuery } from "src/api/services/askApi";
import { theme } from "src/assets/theme";
import ToolsDescription from "./components/ToolsDescription";
import FormattedResponse from "./components/FormattedResponse";
import DoneIcon from "@mui/icons-material/Done";
import TypingIndicator from "src/components/TypingIndicator";
import FilePreview from "./components/FilePreview";

const styles = {
  bgcolor: "grey.100",
  borderRadius: 3,
  minWidth: "10%",
  maxWidth: "70%",
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
  p: 2,
  width: "fit-content",
};

type Agents = "vida" | "knowledge";

type Props = {
  question: {
    id: string;
    text: string;
    file: File | null;
  };
  conversationId: string | null;
  onSuccess: () => void;
  agent: Agents;
};

const QuestionAnswerBlock = ({
  question,
  conversationId,
  onSuccess,
  agent,
}: Props) => {
  const { answer } = useSubmitQuestionQuery(
    conversationId != null
      ? { question: question.text, conversationId, agent, file: question.file }
      : skipToken,
    {
      selectFromResult: ({ data }) => {
        return { answer: data?.[question.text] };
      },
    },
  );

  const toolsUsed = useMemo(() => {
    return answer ? answer.callOrder.map((id) => answer.tools[id]) : [];
  }, [answer]);

  useEffect(() => {
    if (answer?.done === true) {
      onSuccess();
    }
  }, [answer?.done, onSuccess]);

  return answer != null ? (
    <Stack width={"100%"} gap={2}>
      {question.file != null ? (
        <Stack alignSelf={"end"} maxWidth={"60%"} gap={0.5}>
          {question.file != null ? <FilePreview file={question.file} /> : null}
        </Stack>
      ) : null}
      <Stack
        alignSelf={"end"}
        p={2}
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: "10px",
          minWidth: 0,
          maxWidth: "60%",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        {answer.question}
      </Stack>
      {agent === "vida" ? (
        <ToolsDescription tools={toolsUsed} runDone={answer.done} />
      ) : null}
      {answer.error.length > 0 ? (
        <Typography color="error">{answer.error}</Typography>
      ) : null}
      {answer.answer.length === 0 ? null : (
        <FormattedResponse sx={agent === "knowledge" ? styles : null}>
          {answer.answer}
        </FormattedResponse>
      )}
      {agent === "knowledge" && answer.done ? (
        <Typography variant="subtitle2">Vida - AI Agent</Typography>
      ) : null}
      {answer.done ? (
        agent === "vida" ? (
          <DoneIcon color="success" fontSize="large" />
        ) : null
      ) : (
        <TypingIndicator />
      )}
    </Stack>
  ) : null;
};

export default QuestionAnswerBlock;
